// src/utils/links.js
//
// Единая точка правила «что такое ссылка». Распознаём пять видов:
//   1. http://… , https://…      - веб-ссылки
//   2. mailto:…                   - e-mail в формате URL
//   3. bare-email test@test.ru    - «голый» e-mail
//   4. N:\test\testest test\      - Windows-путь (диск + :\ или :/)
//   5. mail.ru, test.com          - bare-домен (TLD по списку IANA)
//
// Запись содержит:
//   url     - то, что уходит в href (у bare-email добавляется mailto:,
//             у bare-домена - https://, у path - исходный путь без file:///)
//   display - то, что видит пользователь (как напечатал автор)
//   kind    - 'url' | 'mailto' | 'path'
//   index, length - позиция и длина в исходной строке
//
// Схема href - из allowlist (http:, https:, mailto:). Дедупликации нет.

import { isKnownTld } from "./tlds";

export const ALLOWED_SCHEMES = Object.freeze(["http:", "https:", "mailto:"]);

const URL_RE = /(?<![\p{L}\p{N}_])(?:https?:\/\/|mailto:)\S+/giu;

const EMAIL_RE =
  /(?<![\p{L}\p{N}._%+\-@])[\p{L}\p{N}_%+-][\p{L}\p{N}._%+-]*@[\p{L}\p{N}.-]+\.[\p{L}]{2,}(?![\p{L}\p{N}_\-@])/giu;

// Промежуточные сегменты допускают пробелы (N:\Program Files\...),
// финальный - нет (иначе «n:\test\file.docx и ещё» съест хвост).
const WINDOWS_PATH_RE =
  /(?<![\p{L}\p{N}_])[A-Za-z]:[\\/](?:[^\\/\s]+(?:\s[^\\/\s]+)*[\\/])*(?:[^\\/\s]+)?/gu;

const BARE_DOMAIN_RE =
  /(?<![\p{L}\p{N}_\-@])[\p{L}\p{N}][\p{L}\p{N}-]*(?:\.[\p{L}\p{N}][\p{L}\p{N}-]*)*\.[\p{L}]{2,}(?![\p{L}\p{N}_\-@])/giu;

// TLD, совпадающие с популярными расширениями файлов - не считаем доменом.
const FILE_EXTENSION_LIKE_TLDS = new Set(["zip", "mov", "sh", "pl", "rs"]);

const TRAILING_PUNCT = new Set([".", ",", ";", ":", "!", "?", "]", "}"]);

function trimUrl(rawUrl) {
  let url = rawUrl;
  while (url.length > 0) {
    const last = url[url.length - 1];
    if (last === ")") {
      const inner = url.slice(0, -1);
      let depth = 0;
      for (let i = 0; i < inner.length; i++) {
        const c = inner[i];
        if (c === "(") depth++;
        else if (c === ")") depth--;
      }
      if (depth > 0) break;
      url = inner;
    } else if (TRAILING_PUNCT.has(last)) {
      url = url.slice(0, -1);
    } else {
      break;
    }
  }
  return url;
}

function schemeOf(url) {
  const i = url.indexOf(":");
  if (i === -1) return "";
  return url.slice(0, i + 1).toLowerCase();
}

function withLowercaseScheme(url) {
  const i = url.indexOf(":");
  if (i === -1) return url;
  return url.slice(0, i).toLowerCase() + url.slice(i);
}

function overlapsAny(start, end, matches) {
  for (const m of matches) {
    if (start < m.index + m.length && end > m.index) return true;
  }
  return false;
}

function collectUrlMatches(text) {
  const result = [];
  URL_RE.lastIndex = 0;
  let match;
  while ((match = URL_RE.exec(text)) !== null) {
    const trimmed = trimUrl(match[0]);
    if (!trimmed) continue;
    const scheme = schemeOf(trimmed);
    if (!ALLOWED_SCHEMES.includes(scheme)) continue;
    result.push({
      url: withLowercaseScheme(trimmed),
      display: trimmed,
      kind: scheme === "mailto:" ? "mailto" : "url",
      index: match.index,
      length: trimmed.length,
    });
  }
  return result;
}

function collectPathMatches(text, alreadyFound) {
  const result = [];
  WINDOWS_PATH_RE.lastIndex = 0;
  let match;
  while ((match = WINDOWS_PATH_RE.exec(text)) !== null) {
    const trimmed = trimUrl(match[0]);
    if (!trimmed) continue;
    const start = match.index;
    const end = start + trimmed.length;
    if (overlapsAny(start, end, alreadyFound)) continue;
    result.push({
      url: trimmed,
      display: trimmed,
      kind: "path",
      index: start,
      length: trimmed.length,
    });
  }
  return result;
}

function collectEmailMatches(text, alreadyFound) {
  const result = [];
  EMAIL_RE.lastIndex = 0;
  let match;
  while ((match = EMAIL_RE.exec(text)) !== null) {
    const raw = match[0];
    const start = match.index;
    const end = start + raw.length;
    if (overlapsAny(start, end, alreadyFound)) continue;
    result.push({
      url: "mailto:" + raw,
      display: raw,
      kind: "mailto",
      index: start,
      length: raw.length,
    });
  }
  return result;
}

function collectBareDomainMatches(text, alreadyFound) {
  const result = [];
  BARE_DOMAIN_RE.lastIndex = 0;
  let match;
  while ((match = BARE_DOMAIN_RE.exec(text)) !== null) {
    const raw = trimUrl(match[0]);
    if (!raw) continue;
    const start = match.index;
    const end = start + raw.length;
    if (overlapsAny(start, end, alreadyFound)) continue;

    const lastDot = raw.lastIndexOf(".");
    if (lastDot === -1) continue;
    const tld = raw.slice(lastDot + 1).toLowerCase();
    if (!isKnownTld(tld)) continue;
    if (FILE_EXTENSION_LIKE_TLDS.has(tld)) continue;

    result.push({
      url: "https://" + raw,
      display: raw,
      kind: "url",
      index: start,
      length: raw.length,
    });
  }
  return result;
}

/**
 * Все ссылки, email, Windows-пути и bare-домены в тексте, в порядке
 * появления. Приоритет при пересечении: URL → path → email → домен.
 */
export function findLinks(text) {
  if (!text) return [];
  const str = String(text);
  const urls = collectUrlMatches(str);
  const paths = collectPathMatches(str, urls);
  const emails = collectEmailMatches(str, [...urls, ...paths]);
  const domains = collectBareDomainMatches(str, [...urls, ...paths, ...emails]);
  return [...urls, ...paths, ...emails, ...domains].sort(
    (a, b) => a.index - b.index,
  );
}

/** Тонкая обёртка: массив href'ов (для счётчика кнопки). */
export function extractLinks(text) {
  return findLinks(text).map((l) => l.url);
}
